import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface UserSequence {
  id: string;
  title: string;
  name: string;
  description?: string;
  image_url?: string;
  album_cover_url?: string;
  file_url?: string;
  price: number;
  is_free: boolean;
  duration?: number;
  software_type?: string;
  created_at: string;
  updated_at: string;
  status: 'pending' | 'approved' | 'rejected';
  artist_name?: string;
  genre?: string;
  holiday?: string;
  total_downloads: number;
  total_revenue: number;
  display_usage_count: number;
}

export interface SequenceAnalytics {
  total_sequences: number;
  total_downloads: number;
  total_revenue: number;
  avg_rating: number;
  downloads_by_year: { year: number; downloads: number }[];
  downloads_by_month: { month: string; downloads: number }[];
  revenue_by_month: { month: string; revenue: number }[];
  popular_sequences: { id: string; title: string; downloads: number }[];
  display_usage_by_year: { year: number; displays: number }[];
}

export const useUserSequences = () => {
  const [sequences, setSequences] = useState<UserSequence[]>([]);
  const [analytics, setAnalytics] = useState<SequenceAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();

  const fetchUserSequences = async () => {
    if (!user) {
      setSequences([]);
      setAnalytics(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Fetch user's sequences (simplified query)
      const { data: userSequences, error: sequencesError } = await supabase
        .from('sequences')
        .select(`
          id,
          title,
          name,
          description,
          image_url,
          album_cover_url,
          file_url,
          price,
          is_free,
          duration,
          software_type,
          created_at,
          updated_at,
          user_id,
          artists (name)
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (sequencesError) throw sequencesError;

      // Get download counts for all user sequences
      const sequenceIds = (userSequences || []).map(seq => seq.id);
      
      // Only run analytics queries if user has sequences
      let downloadCounts = { data: [] };
      let purchaseCounts = { data: [] };
      let displayUsageCounts = { data: [] };
      
      if (sequenceIds.length > 0) {
        try {
          [downloadCounts, purchaseCounts, displayUsageCounts] = await Promise.all([
            // Get download counts
            supabase
              .from('download_logs')
              .select('sequence_id')
              .in('sequence_id', sequenceIds),
            
            // Get purchase data
            supabase
              .from('purchases')
              .select('sequence_id, amount_paid')
              .in('sequence_id', sequenceIds)
              .eq('status', 'completed'),
            
            // Get display usage counts - handle gracefully if table access fails
            supabase
              .from('user_display_lists')
              .select('sequence_id')
              .in('sequence_id', sequenceIds)
              .then(result => {
                // If access fails due to RLS or other issues, return empty data
                if (result.error) {
                  console.warn('Could not access user_display_lists table:', result.error.message);
                  return { data: [] };
                }
                return result;
              })
          ]);
        } catch (error) {
          console.warn('Error fetching analytics data:', error);
          // Continue with empty data if queries fail
          downloadCounts = { data: [] };
          purchaseCounts = { data: [] };
          displayUsageCounts = { data: [] };
        }
      }

      // Process and aggregate the data
      const downloadCountsMap = new Map<string, number>();
      const revenueMap = new Map<string, number>();
      const displayUsageMap = new Map<string, number>();

      // Count downloads per sequence
      (downloadCounts.data || []).forEach((log: any) => {
        const count = downloadCountsMap.get(log.sequence_id) || 0;
        downloadCountsMap.set(log.sequence_id, count + 1);
      });

      // Sum revenue per sequence
      (purchaseCounts.data || []).forEach((purchase: any) => {
        const revenue = revenueMap.get(purchase.sequence_id) || 0;
        revenueMap.set(purchase.sequence_id, revenue + (purchase.amount_paid || 0));
      });

      // Count display usage per sequence
      (displayUsageCounts.data || []).forEach((usage: any) => {
        const count = displayUsageMap.get(usage.sequence_id) || 0;
        displayUsageMap.set(usage.sequence_id, count + 1);
      });

      // Process sequences data
      const processedSequences: UserSequence[] = (userSequences || []).map(seq => {
        return {
          id: seq.id,
          title: seq.title || seq.name,
          name: seq.name,
          description: seq.description,
          image_url: seq.image_url,
          album_cover_url: seq.album_cover_url,
          file_url: seq.file_url,
          price: seq.price || 0,
          is_free: seq.is_free || false,
          duration: seq.duration,
          software_type: seq.software_type,
          created_at: seq.created_at,
          updated_at: seq.updated_at,
          status: 'approved', // Default status since field doesn't exist yet
          artist_name: (seq as any).artists?.name,
          genre: undefined, // Field doesn't exist yet
          holiday: undefined, // Field doesn't exist yet
          total_downloads: downloadCountsMap.get(seq.id) || 0,
          total_revenue: revenueMap.get(seq.id) || 0,
          display_usage_count: displayUsageMap.get(seq.id) || 0,
        };
      });

      setSequences(processedSequences);

      // Calculate analytics
      const totalSequences = processedSequences.length;
      const totalDownloads = processedSequences.reduce((sum, seq) => sum + seq.total_downloads, 0);
      const totalRevenue = processedSequences.reduce((sum, seq) => sum + seq.total_revenue, 0);

      // Only fetch analytics data if user has sequences
      let downloadsByYear = [2020, 2021, 2022, 2023, 2024, 2025].map(year => ({ year, downloads: 0 }));
      let displayUsageByYear = [2020, 2021, 2022, 2023, 2024, 2025].map(year => ({ year, displays: 0 }));
      let revenueByMonth = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map(month => ({ month, revenue: 0 }));
      let downloadsByMonth = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map(month => ({ month, downloads: 0 }));

      if (processedSequences.length > 0) {
        // Get downloads by year (2020-2025)
        downloadsByYear = await Promise.all(
          [2020, 2021, 2022, 2023, 2024, 2025].map(async (year) => {
            const { data, error } = await supabase
              .from('download_logs')
              .select('id')
              .in('sequence_id', processedSequences.map(s => s.id))
              .gte('created_at', `${year}-01-01`)
              .lt('created_at', `${year + 1}-01-01`);
            
            return { year, downloads: data?.length || 0 };
          })
        );

        // Get display usage by year
        try {
          displayUsageByYear = await Promise.all(
            [2020, 2021, 2022, 2023, 2024, 2025].map(async (year) => {
              try {
                const { data, error } = await supabase
                  .from('user_display_lists')
                  .select('id')
                  .in('sequence_id', processedSequences.map(s => s.id))
                  .eq('year', year);
                
                if (error) {
                  console.warn(`Could not access user_display_lists for year ${year}:`, error.message);
                  return { year, displays: 0 };
                }
                
                return { year, displays: data?.length || 0 };
              } catch (error) {
                console.warn(`Error fetching display usage for year ${year}:`, error);
                return { year, displays: 0 };
              }
            })
          );
        } catch (error) {
          console.warn('Error fetching display usage by year:', error);
          displayUsageByYear = [2020, 2021, 2022, 2023, 2024, 2025].map(year => ({ year, displays: 0 }));
        }

        // Get revenue by month for current year
        const currentYear = new Date().getFullYear();
        revenueByMonth = await Promise.all(
          Array.from({ length: 12 }, (_, i) => i + 1).map(async (month) => {
            const monthStart = `${currentYear}-${month.toString().padStart(2, '0')}-01`;
            const monthEnd = month === 12 
              ? `${currentYear + 1}-01-01` 
              : `${currentYear}-${(month + 1).toString().padStart(2, '0')}-01`;

            const { data, error } = await supabase
              .from('purchases')
              .select('amount_paid')
              .in('sequence_id', processedSequences.map(s => s.id))
              .eq('status', 'completed')
              .gte('created_at', monthStart)
              .lt('created_at', monthEnd);

            const monthRevenue = data?.reduce((sum, p) => sum + (p.amount_paid || 0), 0) || 0;
            const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            
            return { month: monthNames[month - 1], revenue: monthRevenue };
          })
        );

        // Get downloads by month for trailing 12 months
        const now = new Date();
        downloadsByMonth = await Promise.all(
          Array.from({ length: 12 }, (_, i) => {
            const monthDate = new Date(now.getFullYear(), now.getMonth() - 11 + i, 1);
            return monthDate;
          }).map(async (monthDate) => {
            const year = monthDate.getFullYear();
            const month = monthDate.getMonth() + 1;
            const monthStart = `${year}-${month.toString().padStart(2, '0')}-01`;
            const monthEnd = month === 12 
              ? `${year + 1}-01-01` 
              : `${year}-${(month + 1).toString().padStart(2, '0')}-01`;

            const { data, error } = await supabase
              .from('download_logs')
              .select('id')
              .in('sequence_id', processedSequences.map(s => s.id))
              .gte('created_at', monthStart)
              .lt('created_at', monthEnd);

            const monthDownloads = data?.length || 0;
            const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            
            return { month: monthNames[month - 1], downloads: monthDownloads };
          })
        );
      }

      // Popular sequences (top 5)
      const popularSequences = processedSequences
        .sort((a, b) => b.total_downloads - a.total_downloads)
        .slice(0, 5)
        .map(seq => ({
          id: seq.id,
          title: seq.title,
          downloads: seq.total_downloads
        }));

      setAnalytics({
        total_sequences: totalSequences,
        total_downloads: totalDownloads,
        total_revenue: totalRevenue,
        avg_rating: 0, // TODO: Implement ratings
        downloads_by_year: downloadsByYear,
        downloads_by_month: downloadsByMonth,
        revenue_by_month: revenueByMonth,
        popular_sequences: popularSequences,
        display_usage_by_year: displayUsageByYear,
      });

    } catch (err) {
      console.error('Error fetching user sequences:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch sequences');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserSequences();
  }, [user]);

  return {
    sequences,
    analytics,
    loading,
    error,
    refetch: fetchUserSequences
  };
};